/**
 * The first-run flow: a shared world with sixteen slots, so a new player
 * names a cat and it becomes theirs.
 *
 * This card replaces the title while a player names their cat. The Play
 * button claims the cat and enters the world without another start gesture.
 * Returning players restore their identity directly.
 *
 * The one hazard is the same one the customizer panel documents: main.js
 * listens on window for keydown (WASD starts and steers the game), so typing
 * a cat named "Wanda" must not walk an unnamed cat across the lawn.  Every
 * bubbling event the game consumes is stopped at the card's root.
 */
import { NAME_MAX } from "@catworld/shared";
import { DENY } from "@catworld/shared";
import { loadLast } from "./customizer/store.js";

/** Bubbling events main.js binds on window, stopped at the card root.
    mouseup stays unstopped for the same reason panel.js leaves it: the
    drag-look flag must always clear. */
const ISOLATE = ["keyup", "pointerdown", "mousedown", "wheel", "touchstart"];

/**
 * Refusals as sentences a child can act on, not codes.  Keyed by the wire
 * values so a why this client has never heard of falls through to a generic
 * line instead of printing "undefined" at somebody.
 */
const SENTENCES = {
  // Two truths for FULL, keyed by the deny's `open` flag: whether a bed
  // can EVER free is server configuration, and this copy must not guess.
  // Neither sentence promises an outcome; each states the rule that
  // refused you.  (The old sentence promised "one will be free" -- and
  // for the street's first ten days no code path could free one.)
  [DENY.FULL]:
    "All sixteen beds on the street are taken, and every cat here " +
    "keeps its bed. There isn’t room for a new cat right now.",
  [DENY.FULL + ":open"]:
    "All sixteen beds are taken by cats whose people were here " +
    "recently. A bed opens when a cat hasn’t visited in a long while " +
    "— please try again later.",
  [DENY.STALE]:
    "The street doesn’t remember your old cat any more. " +
    "Name a new one and it will be yours.",
  [DENY.BUSY]:
    "Your cat is already awake in another window. " +
    "Play it there, or close that window and try again.",
  [DENY.VERSION]:
    "This street has been updated since this page loaded. " +
    "Refresh the page and your cat can move right back in.",
  // Client-local display key, never a wire value: net/index.js shows this
  // INSTEAD of DENY.VERSION when the desktop bridge is present, where
  // "refresh" would be advice a Steam binary cannot take.  The literal
  // must match net/index.js's VERSION_STEAM.
  "version-steam":
    "This world has been updated — update Cat World on Steam " +
    "to visit. Your own street is still open, and still yours.",
};
const SENTENCE_FALLBACK =
  "The street couldn’t take a new cat just now. Try again in a moment.";

export class Join {
  constructor(el, { openCustomizer, onOpen } = /** @type {{openCustomizer?: HTMLElement, onOpen?: () => void}} */ ({})) {
    this.el = el;
    this.name = el.querySelector("#join-name");
    this.deny = el.querySelector("#join-deny");
    this.resolve = null;
    this.onOpen = onOpen;

    // The cap is the shared NAME_MAX, set here rather than as a maxlength
    // attribute in index.html -- an attribute would be a second copy of the
    // constant, and the input is the one place the limit must actually bind.
    this.name.maxLength = NAME_MAX;

    for (const type of ISOLATE) {
      el.addEventListener(type, (e) => e.stopPropagation());
    }
    el.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") this.commit();
    });

    el.querySelector("#join-play").addEventListener("click", () => this.commit());
    // "Build your cat" is a route into the customizer that already exists,
    // through the same button the rest of the game uses -- never a second
    // implementation.  The customizer remembers the look as it is edited
    // (rememberLast), and commit() reads it back, so building first and
    // naming second Just Works in either order.
    el.querySelector("#join-build").addEventListener("click", () => {
      openCustomizer?.click();
    });
  }

  get isOpen() {
    return !this.el.classList.contains("hidden");
  }

  /**
   * Show the card and resolve { name, look } when the player commits.
   *
   * Callable again after a deny: each call re-arms the resolver, and main.js
   * awaits only the latest, so the ask/deny/ask loop is just a loop.  An
   * empty name is allowed through on purpose -- the protocol says a missing
   * name is legal and the server names the cat -- because a child stuck on
   * "it won't let me play" is worse than a cat called Cat.
   */
  ask({ suggestedName } = {}) {
    this.deny.hidden = true;
    if (!this.name.value) {
      const last = suggestedName ?? loadLast()?.name ?? "";
      this.name.value = String(last).slice(0, NAME_MAX);
    }
    this.el.classList.remove("hidden");
    this.onOpen?.();
    this.name.focus({ preventScroll: true });
    return new Promise((res) => { this.resolve = res; });
  }

  commit() {
    if (!this.resolve) return;         // hidden, or already committed
    const res = this.resolve;
    this.resolve = null;
    res({
      name: this.name.value.trim().slice(0, NAME_MAX),
      // The in-progress customizer look, or null for "server default".  Read
      // at commit time, not ask time, so a look built while the card was up
      // is the look that joins.
      look: loadLast(),
    });
  }

  /** why is a DENY.* from shared/protocol.js.  The card stays up: FULL can
      free up, STALE and BUSY are fixed by what the sentence says to do. */
  showDenied(why, { open = false } = {}) {
    this.deny.textContent =
      (open ? SENTENCES[why + ":open"] : null) ?? SENTENCES[why] ?? SENTENCE_FALLBACK;
    this.deny.hidden = false;
    this.el.classList.remove("hidden");
    this.onOpen?.();
  }

  hide() {
    this.el.classList.add("hidden");
    this.resolve = null;
    // Drop focus so the next keystroke belongs to the game, not to a hidden
    // input that would quietly eat the W of "press W to play".
    if (this.el.contains(document.activeElement)) document.activeElement.blur();
  }
}
